import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { OrganizationAccessLevel } from "@overmap-ai/common"
import { selectOrganizationById, selectUsersOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi } from "@tanstack/react-router"
import { memo } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { InviteToOrganizationDialog } from "../../components/Dialogs/InviteToOrganizationDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useOrganizationUserTable, useOrganizationUserTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { userFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/organization/members")

export const OrganizationSettingsMembersView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const organization = useAppSelector(selectOrganizationById(organizationId))

	if (!organization) {
		throw new Error(`Expected organization to be defined, but got ${organization}`)
	}

	const organizationUsers = useShallowEqaulSelector(selectUsersOfOrganization(organization.uuid))

	const [filteredOrganizationUsers, filterValue, setFilterValue] = useTextFilter(
		organizationUsers,
		userFilterCallback,
	)

	const columns = useOrganizationUserTableColumns()

	const table = useOrganizationUserTable({
		data: filteredOrganizationUsers,
		columns,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{organization.name} members</Heading>
					<Text size="sm" accentColor="base">
						Manage organization members and their access levels.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 w-full grow flex-col gap-2">
					<div className="flex h-max w-full items-center justify-between">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
							<InviteToOrganizationDialog organizationId={organizationId}>
								<Button variant="surface" size="sm">
									<LuIcon icon={modelIconMapping.add} />
									Invite
								</Button>
							</InviteToOrganizationDialog>
						</OrganizationAccessRequired>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							size="sm"
							className="w-full"
							variant="surface"
							table={{ ...table }}
							interactive={false}
							emptyText="No users."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
OrganizationSettingsMembersView.displayName = "OrganizationSettingsMembersView"
